/**
 * Export Performance Configuration & Monitoring
 *
 * This module defines performance thresholds and provides utilities
 * for detecting performance regressions during video export.
 *
 * Key metrics:
 * - yieldTime: Time spent waiting for encoder/GPU (should be LOW)
 * - drawTime: Time spent rendering frames (depends on visualizer complexity)
 * - fps: Frames exported per second (higher is better)
 *
 * Historical context (from performance-summary.md):
 * - Spheres WebGL: 70-110 fps at 1080p (with correct thresholds)
 * - WebGPU 4K: ~30 fps (with pixelRatio=1 fix)
 * - Regression symptom: yield time > 50ms, fps < 20
 */

/**
 * Performance thresholds by context type and resolution
 * These are based on documented successful exports.
 */
export const PERFORMANCE_THRESHOLDS = {
  // WebGPU visualizers (pointlights, spheres3d-webgpu)
  webgpu: {
    '1920x1080': {
      minFps: 25,           // Minimum acceptable fps
      maxYieldMs: 20,       // Max avg yield time per frame
      maxDrawMs: 50,        // Max avg draw time per frame
      targetFps: 30,        // Expected fps under normal conditions
    },
    '3840x2160': {
      minFps: 15,
      maxYieldMs: 40,
      maxDrawMs: 100,
      targetFps: 20,
    },
    '1280x720': {
      minFps: 50,
      maxYieldMs: 10,
      maxDrawMs: 20,
      targetFps: 60,
    },
  },

  // WebGL visualizers (spheres3d legacy, if any)
  webgl: {
    '1920x1080': {
      minFps: 60,           // WebGL should be faster (no GPU readback issues)
      maxYieldMs: 5,        // Very low - no flush stalls with high thresholds
      maxDrawMs: 10,
      targetFps: 100,
    },
    '1280x720': {
      minFps: 80,
      maxYieldMs: 3,
      maxDrawMs: 5,
      targetFps: 120,
    },
  },

  // 2D Canvas visualizers (bars, bubbles)
  '2d': {
    '1920x1080': {
      minFps: 80,
      maxYieldMs: 5,
      maxDrawMs: 5,
      targetFps: 120,
    },
    '1280x720': {
      minFps: 100,
      maxYieldMs: 3,
      maxDrawMs: 3,
      targetFps: 150,
    },
  },
};

/**
 * Backpressure thresholds - CRITICAL for performance
 * These values were tuned through testing (see performance-summary.md)
 *
 * WARNING: Changing these can cause severe performance regressions!
 * The commit b4316b8 accidentally reduced WebGL thresholds from 400/250 to 30/15,
 * causing a regression from 70-110 fps to 14-16 fps.
 */
export const BACKPRESSURE_THRESHOLDS = {
  webgpu: {
    // NOTE: Previous values (hardQueue: 24, useFlush: true) caused 600-1200ms flush stalls
    // But hardQueue: 400 caused queue to grow to 1000+ frames (all encoding at end)
    // Middle ground: moderate thresholds with light yields to let encoder catch up
    hardQueue: 60,    // Yield when queue exceeds this (but don't flush)
    softQueue: 30,    // Light yield to prevent rapid buildup
    useFlush: false,  // CRITICAL: Don't use flush() - it stalls 600-1200ms on WebGPU
  },
  webgl: {
    hardQueue: 400,   // WebGL needs MUCH higher thresholds
    softQueue: 250,   // Flush stalls dominate otherwise
    useFlush: false,  // Use light yields, NOT flush()
  },
  '2d': {
    hardQueue: 50,
    softQueue: 25,
    useFlush: false,
  },
};

/**
 * Encoder configuration that affects performance
 */
export const ENCODER_CONFIG = {
  // 'realtime' for lower latency, 'quality' for better output (but slower flush)
  // Regression note: b4316b8 changed this to 'quality' which hurt performance
  latencyMode: 'realtime',

  // Cooperative yield frequency (frames between yields for UI responsiveness)
  // Lower = more responsive UI but more overhead
  // Higher = better throughput but less responsive
  coopYieldInterval: 60,
};

/**
 * Get backpressure thresholds for a given context type
 * @param {string} contextType - 'webgpu', 'webgl', or '2d'
 * @returns {Object} { hardQueue, softQueue, useFlush }
 */
export function getBackpressureThresholds(contextType) {
  const normalized = contextType?.toLowerCase() || '2d';
  return BACKPRESSURE_THRESHOLDS[normalized] || BACKPRESSURE_THRESHOLDS['2d'];
}

/**
 * Get performance thresholds for a given context type and resolution
 * @param {string} contextType - 'webgpu', 'webgl', or '2d'
 * @param {string} resolution - e.g., '1920x1080'
 * @returns {Object} { minFps, maxYieldMs, maxDrawMs, targetFps }
 */
export function getPerformanceThresholds(contextType, resolution) {
  const normalized = contextType?.toLowerCase() || '2d';
  const thresholds = PERFORMANCE_THRESHOLDS[normalized];
  if (!thresholds) {
    return PERFORMANCE_THRESHOLDS['2d']['1920x1080'];
  }
  return thresholds[resolution] || thresholds['1920x1080'];
}

/**
 * Performance monitor for export process
 * Collects metrics and can detect regressions
 */
export class ExportPerformanceMonitor {
  constructor(contextType, resolution, options = {}) {
    this.contextType = contextType;
    this.resolution = resolution;
    this.thresholds = getPerformanceThresholds(contextType, resolution);
    this.onWarning = options.onWarning || console.warn;

    // Accumulated metrics
    this.samples = [];
    this.warnings = [];
    this.startTime = null;
    this.frameCount = 0;
  }

  /**
   * Start monitoring
   */
  start() {
    this.startTime = performance.now();
    this.samples = [];
    this.warnings = [];
    this.frameCount = 0;
  }

  /**
   * Record a frame's metrics
   * @param {Object} metrics - { drawMs, yieldMs, vfMs, encodeMs }
   */
  recordFrame(metrics) {
    this.frameCount++;
    this.samples.push({
      timestamp: performance.now() - this.startTime,
      ...metrics,
    });

    // Check for immediate issues (every 100 frames)
    if (this.frameCount % 100 === 0) {
      this.checkForRegressions();
    }
  }

  /**
   * Check recent samples for performance regressions
   */
  checkForRegressions() {
    const recentSamples = this.samples.slice(-100);
    if (recentSamples.length < 50) return;

    const avgYield = recentSamples.reduce((sum, s) => sum + (s.yieldMs || 0), 0) / recentSamples.length;
    const avgDraw = recentSamples.reduce((sum, s) => sum + (s.drawMs || 0), 0) / recentSamples.length;

    // Calculate fps from timestamps
    const timeSpan = recentSamples[recentSamples.length - 1].timestamp - recentSamples[0].timestamp;
    const fps = timeSpan > 0 ? (recentSamples.length / timeSpan) * 1000 : 0;

    // Check thresholds
    if (avgYield > this.thresholds.maxYieldMs) {
      const warning = `[Perf] HIGH YIELD TIME: ${avgYield.toFixed(1)}ms avg (threshold: ${this.thresholds.maxYieldMs}ms). Check backpressure thresholds.`;
      if (!this.warnings.includes('yield')) {
        this.warnings.push('yield');
        this.onWarning(warning);
      }
    }

    if (avgDraw > this.thresholds.maxDrawMs) {
      const warning = `[Perf] HIGH DRAW TIME: ${avgDraw.toFixed(1)}ms avg (threshold: ${this.thresholds.maxDrawMs}ms). Visualizer may be too complex.`;
      if (!this.warnings.includes('draw')) {
        this.warnings.push('draw');
        this.onWarning(warning);
      }
    }

    if (fps < this.thresholds.minFps) {
      const warning = `[Perf] LOW FPS: ${fps.toFixed(1)} (minimum: ${this.thresholds.minFps}). Export performance regression detected!`;
      if (!this.warnings.includes('fps')) {
        this.warnings.push('fps');
        this.onWarning(warning);
      }
    }
  }

  /**
   * Get final report
   * @returns {Object} Performance summary
   */
  getReport() {
    if (this.samples.length === 0) {
      return { error: 'No samples collected' };
    }

    const totalTime = performance.now() - this.startTime;
    const avgYield = this.samples.reduce((sum, s) => sum + (s.yieldMs || 0), 0) / this.samples.length;
    const avgDraw = this.samples.reduce((sum, s) => sum + (s.drawMs || 0), 0) / this.samples.length;
    const avgVf = this.samples.reduce((sum, s) => sum + (s.vfMs || 0), 0) / this.samples.length;
    const fps = (this.samples.length / totalTime) * 1000;

    const passed =
      fps >= this.thresholds.minFps &&
      avgYield <= this.thresholds.maxYieldMs &&
      avgDraw <= this.thresholds.maxDrawMs;

    return {
      contextType: this.contextType,
      resolution: this.resolution,
      frameCount: this.samples.length,
      totalTimeMs: totalTime,
      fps: fps,
      avgDrawMs: avgDraw,
      avgYieldMs: avgYield,
      avgVideoFrameMs: avgVf,
      thresholds: this.thresholds,
      passed,
      warnings: this.warnings,
    };
  }
}

/**
 * Run a quick sanity check on current thresholds
 * This can be called during development to verify config
 */
export function validateThresholds() {
  const issues = [];

  // Check WebGL thresholds are high enough (learned from regression)
  if (BACKPRESSURE_THRESHOLDS.webgl.hardQueue < 100) {
    issues.push(`WebGL hardQueue (${BACKPRESSURE_THRESHOLDS.webgl.hardQueue}) is too low! Should be >= 400`);
  }
  if (BACKPRESSURE_THRESHOLDS.webgl.softQueue < 50) {
    issues.push(`WebGL softQueue (${BACKPRESSURE_THRESHOLDS.webgl.softQueue}) is too low! Should be >= 250`);
  }

  // Check WebGL doesn't use flush (causes stalls)
  if (BACKPRESSURE_THRESHOLDS.webgl.useFlush) {
    issues.push('WebGL should NOT use flush() - causes stalls');
  }

  // Check latencyMode
  if (ENCODER_CONFIG.latencyMode !== 'realtime') {
    issues.push(`Encoder latencyMode is '${ENCODER_CONFIG.latencyMode}' - 'realtime' is faster`);
  }

  return {
    valid: issues.length === 0,
    issues,
  };
}
